// One label/value line for read-only data (GET-266). Replaces the disabled
// input boxes that used to hold read-only fields. Group rows with DetailGroup,
// which draws the card and drops the hairline under the final row.

import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTheme } from '../../styles/ThemeProvider';

export interface DetailRowProps {
  label: string;
  /** A string or number renders as Text; a node renders as is. */
  value: React.ReactNode;
  /** Plex Mono. Only for money and percentages. */
  mono?: boolean;
  /** The value wraps under the label instead of sitting right-aligned. */
  multiline?: boolean;
  /** Hides the bottom hairline. DetailGroup sets it on its final row. */
  last?: boolean;
  /** Defaults to `detail-row-<label>`. */
  testID?: string;
}

export function DetailRow({
  label,
  value,
  mono = false,
  multiline = false,
  last = false,
  testID,
}: DetailRowProps) {
  const theme = useTheme();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        row: {
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: theme.spacing.md,
          paddingVertical: theme.spacing.md,
          paddingHorizontal: theme.spacing.lg,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
        },
        rowMultiline: {
          flexDirection: 'column',
          justifyContent: 'flex-start',
          gap: theme.spacing.xs,
        },
        rowLast: { borderBottomWidth: 0 },
        label: { ...theme.typography.md, color: theme.colors.textSecondary },
        value: {
          ...theme.typography.md,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
          textAlign: 'right',
          flexShrink: 1,
        },
        valueMultiline: {
          fontFamily: theme.fontFamily.body,
          textAlign: 'left',
        },
        valueMono: { fontFamily: theme.fontFamily.mono },
      }),
    [theme]
  );

  const isText = typeof value === 'string' || typeof value === 'number';

  return (
    <View
      testID={testID ?? `detail-row-${label}`}
      style={[styles.row, multiline && styles.rowMultiline, last && styles.rowLast]}
    >
      <Text style={styles.label}>{label}</Text>
      {isText ? (
        <Text style={[styles.value, multiline && styles.valueMultiline, mono && styles.valueMono]}>
          {value}
        </Text>
      ) : (
        value
      )}
    </View>
  );
}

export default DetailRow;
